import type { CSSProperties, FC } from 'react'

import { useRef } from 'react'
import { cn } from '@/lib/utils'
import { useScrollFrame } from '@/hooks/use-scroll-frame'
import { Photo } from '@/components/kit/photo'
import { Reveal } from '@/components/kit/reveal'

const layers = [
    { src: '/images/social/studio-4.jpg', alt: 'The Updraft team planning a shoot around a table of cameras and sketches', position: '50% 50%', speed: 0.06, rotate: -1.5, delay: 0, className: 'left-[16%] top-[4%] z-10 aspect-[16/10] w-[66%]' },
    { src: '/images/social/studio-2.jpg', alt: 'Phones and colour swatches laid out for a voice and look workshop', position: '50% 40%', speed: 0.2, rotate: -5, delay: 150, className: 'left-0 top-[38%] z-20 aspect-[3/4] w-[29%]' },
    { src: '/images/social/creator-4.jpg', alt: 'A camera on a tripod in the Updraft studio', position: '60% 50%', speed: 0.14, rotate: 4, delay: 250, className: 'right-0 top-[24%] z-20 aspect-[3/4.2] w-[26%]' },
    { src: '/images/work/lifestyle-3.jpg', alt: 'Ceramics styled for a Hearth & Hollow shoot', position: '45% 50%', speed: 0.3, rotate: 2.5, delay: 350, className: 'bottom-0 left-[38%] z-30 aspect-[4/3] w-[30%]' }
]

const Badge: FC = () => {
    return (
        <div className='absolute right-[6%] top-0 z-40 size-28 md:size-36'>
            <div className='relative size-full rounded-full bg-ember'>
                <svg
                    viewBox='0 0 100 100'
                    aria-hidden='true'
                    className='absolute inset-0 size-full animate-spin-slow [animation-duration:16s]'
                >
                    <defs>
                        <path
                            id='badge-circle'
                            d='M50,50 m-37,0 a37,37 0 1,1 74,0 a37,37 0 1,1 -74,0'
                        />
                    </defs>
                    <text className='fill-ink font-mono text-[9.5px] uppercase tracking-[0.2em]'>
                        <textPath href='#badge-circle'>
                            Updraft studio · est. 2019 · catch the lift ·
                        </textPath>
                    </text>
                </svg>
                <span className='absolute inset-0 flex items-center justify-center font-heading text-2xl font-bold tracking-[-0.035em] text-ink md:text-3xl'>
                    U.
                </span>
            </div>
        </div>
    )
}

export const Collage: FC = () => {
    const containerRef = useRef<HTMLDivElement>(null)
    const layerRefs = useRef<Array<HTMLDivElement | null>>([])

    useScrollFrame(() => {
        const container = containerRef.current

        if (!container || window.matchMedia('(prefers-reduced-motion: reduce)').matches) return

        const rect = container.getBoundingClientRect()
        const offset = rect.top + rect.height / 2 - window.innerHeight / 2

        layerRefs.current.forEach((layer, index) => {
            if (layer) layer.style.transform = `translate3d(0, ${(-offset * layers[index].speed).toFixed(1)}px, 0)`
        })
    })

    return (
        <div
            ref={containerRef}
            className='relative mx-auto h-[clamp(380px,64vw,820px)] max-w-[1400px]'
        >
            <Badge />

            {layers.map((layer, index) => (
                <div
                    key={layer.src}
                    ref={element => {
                        layerRefs.current[index] = element
                    }}
                    className={cn('absolute will-change-transform', layer.className)}
                >
                    <Reveal
                        delay={layer.delay}
                        y={50}
                        className='h-full'
                    >
                        <div
                            className='h-full overflow-hidden rounded-[1.25rem] shadow-[0_40px_80px_-40px_rgb(30_30_30/0.55)] md:rounded-[2rem]'
                            style={{ rotate: `${layer.rotate}deg` } as CSSProperties}
                        >
                            <Photo
                                src={layer.src}
                                alt={layer.alt}
                                sizes='(min-width: 1024px) 50vw, 70vw'
                                position={layer.position}
                                preload={index === 0}
                                grain
                                className='h-full w-full'
                            />
                        </div>
                    </Reveal>
                </div>
            ))}
        </div>
    )
}
